import React from "react";
import Icon from "../../../components/AppIcon";
import { useNavigate } from "react-router-dom";

const DailyTroncTable = ({ records, onViewDetails }) => {
  const navigate = useNavigate();

  if (!records || records.length === 0) {
    return (
      <div className="card p-8 text-center">
        <div className="empty-state-icon mx-auto mb-4">
          <Icon name="Calendar" size={32} />
        </div>
        <h3 className="text-lg font-semibold mb-2">No TRONC records found</h3>
        <p className="text-muted-foreground">
          TRONC records will appear here once you start recording daily takings.
        </p>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount || 0);
  };

  const getStatusInfo = (record) => {
    // Status priority: Distributed > Approved > Pending
    if (
      record.isDistributed ||
      record.status === "Distributed" ||
      record.status === 3
    ) {
      return {
        label: "Distributed",
        color: "success",
        icon: "CheckCircle",
        canProcess: false,
      };
    }
    if (record.status === "Approved" || record.status === 2) {
      return {
        label: "Approved",
        color: "info",
        icon: "ThumbsUp",
        canProcess: true,
      };
    }
    // Default to Pending (status 0, 1, or "Pending")
    return {
      label: "Pending",
      color: "warning",
      icon: "Clock",
      canProcess: true,
    };
  };

  // Navigate to distribution page with this day's record pre-selected
  const handleProcess = (record) => {
    // Get the date from the record for the distribution page
    const recordDate = new Date(record.periodStart).toISOString().split("T")[0];
    navigate(`/tronc-distribution?date=${recordDate}&recordId=${record.id}`);
  };

  // View details - navigate to distribution page in view mode
  const handleViewDetails = (record) => {
    const recordDate = new Date(record.periodStart).toISOString().split("T")[0];
    navigate(
      `/tronc-distribution?date=${recordDate}&recordId=${record.id}&view=true`,
    );
  };

  return (
    <div className="card">
      <div className="responsive-table-container">
        <div className="w-full overflow-x-auto">
          <table className="table w-full min-w-[1100px]">
            <thead>
              <tr>
                <th>Date</th>
                <th>Location</th>
                <th>Cash Tips</th>
                <th>Card Tips</th>
                <th>Service Charge</th>
                <th>Total TRONC</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => {
                const statusInfo = getStatusInfo(record);
                const isLocked =
                  record.isDistributed ||
                  record.status === "Distributed" ||
                  record.status === 3;

                // Today's record can only be processed from tomorrow onwards
                const recordDate = new Date(record.periodStart);
                const today = new Date();
                const isToday =
                  recordDate.getFullYear() === today.getFullYear() &&
                  recordDate.getMonth() === today.getMonth() &&
                  recordDate.getDate() === today.getDate();
                const canProcess = statusInfo.canProcess && !isToday;

                return (
                  <tr key={record.id}>
                    <td>
                      <div className="flex items-center gap-2">
                        <Icon
                          name="Calendar"
                          size={16}
                          className="text-muted-foreground"
                        />
                        <span className="font-medium">
                          {formatDate(record.periodStart)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Icon
                          name="MapPin"
                          size={16}
                          className="text-muted-foreground"
                        />
                        <span>{record.locationName || "Unknown"}</span>
                      </div>
                    </td>
                    <td>
                      <span className="font-medium">
                        {formatAmount(record.cashTips)}
                      </span>
                    </td>
                    <td>
                      <span className="font-medium">
                        {formatAmount(record.cardTips)}
                      </span>
                    </td>
                    <td>
                      <span className="font-medium">
                        {formatAmount(record.serviceCharge)}
                      </span>
                    </td>
                    <td>
                      <span className="font-semibold text-primary text-lg">
                        {formatAmount(record.amount)}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className={`badge badge-${statusInfo.color}`}>
                          <Icon
                            name={statusInfo.icon}
                            size={14}
                            className="mr-1"
                          />
                          {statusInfo.label}
                        </span>
                        {record.hasManualOverride && (
                          <span
                            className="badge badge-warning"
                            title="Manual adjustments were made"
                          >
                            <Icon name="AlertCircle" size={12} />
                          </span>
                        )}
                        {isLocked && (
                          <Icon
                            name="Lock"
                            size={14}
                            className="text-muted-foreground"
                            title="Record is locked (distributed)"
                          />
                        )}
                      </div>
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canProcess ? (
                          <button
                            onClick={() => handleProcess(record)}
                            className="btn btn-sm btn-primary"
                            title="Open in Distribution page"
                          >
                            <Icon name="PieChart" size={16} />
                            <span className="ml-1">Process</span>
                          </button>
                        ) : isToday && statusInfo.canProcess ? (
                          <button
                            disabled
                            className="btn btn-sm btn-outline opacity-50 cursor-not-allowed"
                            title="Today's record can only be processed from tomorrow onwards"
                          >
                            <Icon name="Clock" size={16} />
                            <span className="ml-1">Available Tomorrow</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => handleViewDetails(record)}
                            className="btn btn-sm btn-outline"
                            title="View distribution details"
                          >
                            <Icon name="Eye" size={16} />
                            <span className="ml-1">View</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DailyTroncTable;
